// src/lib/janetNotifyQueue.js — which of Janet's conversations have a NEW
// item since the last look, one entry per new item — not per session, and
// not collapsed into a single "N new items" summary (Aaron, 2026-10-21:
// "each additional item should get a notification from the iphone and the
// desktop OS"). Pure.
//
// A session's own `last_message_at` only ever reflects its newest message, so
// comparing it against one global high-water mark collapsed several distinct
// Today messages landing in the same thread between polls into a single
// notification, and collapsed several newly-active sessions into one
// ("N new items from Janet"). The durable cursor here is per-session
// (`message_count`, which api/_lib/janet-today.js `postHeadsUp` bumps by
// exactly one per message it writes — see janet-openers.js writeOpenerTopic
// for the other writer, which bumps it from 0 to 1 once its opening message
// lands), not a single scalar, so every message that actually landed since
// the last look is counted and reported on its own. A session the cursor has
// never tracked before — someone just opened it, or it quietly dropped out of
// the unread query for a while — is exactly one new conversation, so it
// always gets exactly one notification regardless of its message_count; a
// row whose message_count can't be read falls back to the old
// last-message-at comparison for that one row (at most one notification)
// rather than going silent.

// A bound on how many native notifications one look shows (the tab asleep
// for hours while a session's message_count climbed a long way). It never
// drops anything: an item past the bound stays BEHIND the cursor — the
// cursor only advances over items actually returned — so the next look
// returns it (Aaron: "each additional item should get a notification").
export const MAX_NOTIFY_PER_LOOK = 10

/**
 * `rows`: unread Janet conversations (id, title, last_message_at,
 * message_count), newest first. `seen`: the per-session cursor from the last
 * look (`{ [sessionId]: { count, lastMessageAt } }`), or null on the first
 * look, which only remembers so opening the app never replays old items.
 * Returns one `notify` entry per new message (each carrying a `tag` unique to
 * that exact message, so a caller using it as a notification tag never has
 * one new message silently replace another's) and the next cursor — which
 * carries forward every session the previous cursor already knew, not only
 * the ones present in this look, so a session leaving the unread list for a
 * while and coming back isn't read as brand new. The cursor moves only past
 * what `notify` carries; anything beyond `limit` waits for the next look.
 */
export function newJanetActivity(rows, seen, { limit = MAX_NOTIFY_PER_LOOK } = {}) {
  const list = (rows || []).filter(r => r?.id && r.last_message_at)
  const firstLook = seen == null
  const prior = seen || {}
  const next = { ...prior }
  const notify = []
  const room = () => notify.length < limit
  for (const r of list) {
    const before = prior[r.id]
    const count = Number.isFinite(r.message_count) ? r.message_count : null
    if (firstLook) { next[r.id] = { count, lastMessageAt: r.last_message_at }; continue }
    if (!before) {
      // A session the cursor never tracked is one new conversation. With no
      // room left it stays untracked, so the next look still reads it as new.
      if (!room()) continue
      notify.push({ id: r.id, title: r.title, tag: `janet-${r.id}-new` })
      next[r.id] = { count, lastMessageAt: r.last_message_at }
    } else if (count != null && Number.isFinite(before.count)) {
      let reached = before.count
      for (let n = before.count + 1; n <= count && room(); n++) {
        notify.push({ id: r.id, title: r.title, tag: `janet-${r.id}-${n}` })
        reached = n
      }
      // Fully caught up → the live values; otherwise only as far as told.
      next[r.id] = reached >= count
        ? { count, lastMessageAt: r.last_message_at }
        : { count: reached, lastMessageAt: before.lastMessageAt }
    } else if (r.last_message_at > (before.lastMessageAt || '')) {
      if (!room()) continue
      notify.push({ id: r.id, title: r.title, tag: `janet-${r.id}-${r.last_message_at}` })
      next[r.id] = { count, lastMessageAt: r.last_message_at }
    } else {
      next[r.id] = { count, lastMessageAt: r.last_message_at }
    }
  }
  return { notify, seen: next }
}

/**
 * Drive the actual showing of pending items one at a time, committing the
 * cursor past an item ONLY once `show(item)` returns without throwing — a
 * transient failure (the OS notification API throwing, or `allowed()`
 * turning false between one item and the next) leaves that item, and
 * everything still queued behind it, UNCONSUMED, so the next look retries
 * rather than silently marking something "told" that was never actually
 * displayed (useJanetDesktopNotifications previously committed the whole
 * batch's cursor up front, before attempting to show anything, so a thrown
 * exception mid-batch was swallowed after the fact with no retry).
 *
 * The one case where consuming without showing is correct, and deliberately
 * unchanged: `seen == null` (the first-ever look). It only remembers where
 * things stand — nothing is shown, so nothing is gated by `allowed()` or
 * `show` at all, the same as `newJanetActivity`'s own first-look behavior.
 *
 * A caller whose notification SETTING is off (as opposed to a mid-drain
 * failure) should check `allowed()` once itself before calling this at all,
 * and fall back to a plain `newJanetActivity(rows, seen).seen` bulk advance
 * — that is the existing, deliberate "don't replay a backlog once
 * re-enabled" behavior, distinct from the failure case this function exists
 * to fix, and is unaffected by anything here.
 */
export function drainJanetActivity(rows, seen, { limit = MAX_NOTIFY_PER_LOOK, allowed = () => true, show } = {}) {
  if (seen == null) return { seen: newJanetActivity(rows, null).seen, shown: 0 }
  let current = seen
  let shown = 0
  for (let i = 0; i < limit; i++) {
    if (!allowed()) break
    const { notify, seen: nextSeen } = newJanetActivity(rows, current, { limit: 1 })
    if (!notify.length) break
    try { show(notify[0]) } catch { break }
    current = nextSeen
    shown++
  }
  return { seen: current, shown }
}

/**
 * One full poll cycle: fetch the rows, then apply the exact same
 * first-look / notifications-off / drain branching
 * useJanetDesktopNotifications used to inline itself — pulled out here so
 * it is a plain function over already-tested pieces (newJanetActivity,
 * drainJanetActivity) instead of logic duplicated inside a React effect.
 *
 * `fetchRows()` must resolve to the ARRAY of unread rows, or `null`/
 * `undefined` on a failed read (Supabase's own convention: `data` is null
 * exactly when `error` is set) — a failed read changes nothing and is
 * retried on the next look, same as before this was extracted.
 */
export async function runDesktopNotifyLook({ fetchRows, seen, allowed = () => true, show }) {
  const rows = await fetchRows()
  if (rows == null) return seen   // a failed read changes nothing
  if (seen == null) return newJanetActivity(rows, null).seen   // first-ever look: only remember
  if (!allowed()) return newJanetActivity(rows, seen).seen     // off by choice: advance without showing
  return drainJanetActivity(rows, seen, { allowed, show }).seen
}

// Returned by a guarded poll call that was skipped because a previous call
// through the SAME guard is still in flight — distinct from any legitimate
// return value (including `null`, which a failed read can legitimately
// produce), so a caller can tell "nothing happened because this was a
// no-op" apart from "nothing happened because the poll genuinely found
// nothing new".
export const POLL_SKIPPED = Symbol('janet-notify-poll-skipped')

/**
 * A reentrancy guard for an async poll loop. The returned `run(fn)` calls
 * `fn()` and returns its result, UNLESS a call made through this SAME guard
 * is still pending, in which case it does nothing and resolves to
 * `POLL_SKIPPED` immediately — never awaiting, never calling `fn` a second
 * time concurrently.
 *
 * This is what stops a poll whose own async work (a slow network
 * round-trip) outlives its own interval period from overlapping with the
 * next tick: without it, two concurrent executions read the same starting
 * cursor and independently compute — and show — the same "new" items
 * before either one commits its result back, which is how one new message
 * could notify twice. The guard releases on every path, including a thrown
 * `fn`, so one failed look never wedges every later one shut.
 */
export function createPollGuard() {
  let inFlight = false
  return async function runGuarded(fn) {
    if (inFlight) return POLL_SKIPPED
    inFlight = true
    try { return await fn() } finally { inFlight = false }
  }
}
